import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import PageLayout from '../../components/layout/PageLayout';

import FilamentStats from './components/FilamentStats';
import FilamentFilters from './components/FilamentFilters';
import FilamentGrid from './components/FilamentGrid';
import AddFilamentDrawer from './components/AddFilamentDrawer';
import EditFilamentDrawer from './components/EditFilamentDrawer';
import FilamentHistoryModal from './components/FilamentHistoryModal';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseHelpers } from '../../lib/supabase';

const FilamentInventory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showAddDrawer, setShowAddDrawer] = useState(false);
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const [filamentToEdit, setFilamentToEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedFilamentForHistory, setSelectedFilamentForHistory] = useState(null);

  // Lista de filamentos carregada da API
  const [filaments, setFilaments] = useState([]);

  // Filter state
  const [filters, setFilters] = useState({
    colors: [],
    materials: [],
    brands: [],
    suppliers: [],
    stockLevels: [],
    search: '',
    notesSearch: '',
    groupDuplicates: false,
    groupBySupplier: false
  });

  // Mapear registro da API para estrutura de UI
  const mapFilamentRecord = (rec) => {
    const originalWeight = parseFloat(rec?.original_weight ?? 1000) || 1000;
    const currentWeight = parseFloat(rec?.stock_grams ?? rec?.currentWeight ?? 0) || 0;
    const pricePerKg = parseFloat(rec?.price_per_kg ?? 0) || 0;
    const costPerGram = pricePerKg > 0 ? (pricePerKg / 1000) : (parseFloat(rec?.costPerGram ?? 0) || 0);
    const rollPrice = pricePerKg > 0 ? (pricePerKg * (originalWeight / 1000)) : (parseFloat(rec?.totalCost ?? 0) || 0);
    return {
      id: rec?.id,
      brand: rec?.brand,
      material: rec?.material,
      diameter: rec?.diameter || '1.75',
      colorName: rec?.colorName || rec?.color || '',
      colorHex: rec?.colorHex || rec?.color_hex || '#A0A0A0',
      originalWeight,
      currentWeight,
      totalCost: rollPrice,
      costPerGram,
      gramsPerMeter: rec?.grams_per_meter,
      density: rec?.density,
      purchaseDate: rec?.purchaseDate || rec?.purchase_date || '',
      createdAt: rec?.created_at || rec?.createdAt,
      image: Array.isArray(rec?.image_urls) ? rec?.image_urls?.[0] : rec?.image,
      imageAlt: rec?.imageAlt,
      image_urls: Array.isArray(rec?.image_urls) ? rec?.image_urls : (rec?.image ? [rec?.image] : []),
      supplier: rec?.supplier || '',
      notes: rec?.notes || ''
    };
  };

  // Carregar filamentos da API
  useEffect(() => {
    const loadFilaments = async () => {
      if (!user?.id) {
        setLoading(false);
        setFilaments([]);
        return;
      }
      setLoading(true);
      try {
        const { data, error } = await supabaseHelpers?.getUserFilaments(user?.id);
        if (!error) {
          const mapped = (data || [])?.map(mapFilamentRecord);
          setFilaments(mapped);
        }
      } catch (err) {
        console.error('Erro ao carregar filamentos:', err);
      } finally {
        setLoading(false);
      }
    };
    loadFilaments();
  }, [user]);

  // Get unique values for filters
  const availableColors = useMemo(() => {
    const colors = filaments?.map((f) => ({ name: f?.colorName, hex: f?.colorHex }));
    return colors?.filter((color, index, self) =>
    index === self?.findIndex((c) => c?.hex === color?.hex)
    );
  }, [filaments]);

  const availableMaterials = useMemo(() => {
    return [...new Set(filaments.map((f) => f.material))];
  }, [filaments]);

  const availableBrands = useMemo(() => {
    return [...new Set(filaments.map((f) => f.brand))];
  }, [filaments]);

  const availableSuppliers = useMemo(() => {
    return [...new Set(filaments.map((f) => (f?.supplier || '')?.trim()))].filter(Boolean);
  }, [filaments]);

  // Filter filaments
  const filteredFilaments = useMemo(() => {
    return filaments?.filter((filament) => {
      // Search filter
      if (filters?.search) {
        const searchTerm = filters?.search?.toLowerCase();
        const searchableText = `${filament?.brand} ${filament?.material} ${filament?.colorName}`?.toLowerCase();
        if (!searchableText?.includes(searchTerm)) return false;
      }

      // Notes search filter
      if (filters?.notesSearch) {
        const notesTerm = filters?.notesSearch?.toLowerCase();
        const notesText = (filament?.notes || '')?.toLowerCase();
        if (!notesText?.includes(notesTerm)) return false;
      }

      // Color filter
      if (filters?.colors?.length > 0 && !filters?.colors?.includes(filament?.colorHex)) {
        return false;
      }

      // Material filter
      if (filters?.materials?.length > 0 && !filters?.materials?.includes(filament?.material)) {
        return false;
      }

      // Brand filter
      if (filters?.brands?.length > 0 && !filters?.brands?.includes(filament?.brand)) {
        return false;
      }

      // Supplier filter
      if (filters?.suppliers?.length > 0 && !filters?.suppliers?.includes((filament?.supplier || '')?.trim())) {
        return false;
      }

      // Stock level filter (by grams)
      if (filters?.stockLevels?.length > 0) {
        const current = parseFloat(filament?.currentWeight || 0);
        const hasMatchingStockLevel = filters?.stockLevels?.some((level) => {
          if (level === 'high_g') return current > 600;
          if (level === 'medium_g') return current >= 200 && current <= 600;
          if (level === 'low_g') return current < 200;
          return false;
        });
        if (!hasMatchingStockLevel) return false;
      }

      return true;
    });
  }, [filaments, filters]);

  // Group identical filaments (brand+material+diameter+color)
  const groupedFilaments = useMemo(() => {
    if (!filters?.groupDuplicates) return filteredFilaments;
    const map = new Map();
    filteredFilaments?.forEach((f) => {
      const baseKey = `${f?.brand}|${f?.material}|${f?.diameter}|${f?.colorHex}`;
      const supKey = `${(f?.supplier || '')?.trim()}`;
      const key = filters?.groupBySupplier ? `${baseKey}|${supKey}` : baseKey;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          brand: f?.brand,
          material: f?.material,
          diameter: f?.diameter,
          colorName: f?.colorName,
          colorHex: f?.colorHex,
          filaments: [],
          totalCurrentWeight: 0,
          totalOriginalWeight: 0,
          weightedAvgCostPerGram: 0,
          image: f?.image,
          image_urls: f?.image_urls
        });
      }
      const group = map.get(key);
      group.filaments.push(f);
      group.totalCurrentWeight += (parseFloat(f?.currentWeight) || 0);
      group.totalOriginalWeight += (parseFloat(f?.originalWeight) || 0);
    });
    // compute weighted average cost per gram
    map.forEach((group) => {
      const totalWeight = group?.filaments?.reduce((sum, f) => sum + (parseFloat(f?.currentWeight) || 0), 0) || 0;
      const weightedCost = group?.filaments?.reduce((sum, f) => sum + ((parseFloat(f?.currentWeight) || 0) * (parseFloat(f?.costPerGram) || 0)), 0) || 0;
      group.weightedAvgCostPerGram = totalWeight > 0 ? (weightedCost / totalWeight) : 0;
    });
    return Array.from(map.values());
  }, [filteredFilaments, filters?.groupDuplicates]);

  const handleAddFilament = async (filamentData) => {
    try {
      if (!user?.id) return;
      const payload = {
        user_id: user?.id,
        brand: filamentData?.brand,
        material: filamentData?.material,
        color: filamentData?.colorName,
        color_hex: filamentData?.colorHex,
        price_per_kg: parseFloat(filamentData?.totalCost) / (parseFloat(filamentData?.originalWeight) / 1000),
        stock_grams: parseFloat(filamentData?.currentWeight),
        image_url: Array.isArray(filamentData?.image_urls) ? filamentData?.image_urls?.[0] : filamentData?.image,
        image_urls: Array.isArray(filamentData?.image_urls) ? filamentData?.image_urls : (filamentData?.image ? [filamentData?.image] : []),
        supplier: filamentData?.supplier || null,
        notes: filamentData?.notes || null,
        purchase_date: filamentData?.purchaseDate || null,
        grams_per_meter: filamentData?.gramsPerMeter ?? filamentData?.grams_per_meter ?? null,
        density: filamentData?.density ?? null,
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString()
      };
      const { data, error } = await supabaseHelpers?.createFilament(payload);
      if (error) {
        alert('Erro ao criar filamento');
        return;
      }
      setFilaments(prev => [...prev, mapFilamentRecord(data)]);
    } catch (err) {
      console.error('Erro ao salvar filamento:', err);
    }
  };

  const handleEditFilament = (filament) => {
    setFilamentToEdit(filament);
    setShowEditDrawer(true);
  };

  const handleSaveFilamentEdit = async (updated) => {
    try {
      const payload = {
        brand: updated?.brand,
        material: updated?.material,
        color: updated?.colorName,
        color_hex: updated?.colorHex,
        price_per_kg: parseFloat(updated?.totalCost) / (parseFloat(updated?.originalWeight) / 1000),
        stock_grams: parseFloat(updated?.currentWeight),
        image_urls: Array.isArray(updated?.image_urls) ? updated?.image_urls : (updated?.image ? [updated?.image] : []),
        image_url: Array.isArray(updated?.image_urls) ? updated?.image_urls?.[0] : updated?.image,
        purchase_date: updated?.purchaseDate || null,
        supplier: updated?.supplier,
        notes: updated?.notes,
        grams_per_meter: updated?.gramsPerMeter ?? updated?.grams_per_meter ?? null,
        density: updated?.density ?? null,
        updated_at: new Date()?.toISOString()
      };
      const { data, error } = await supabaseHelpers?.updateFilament(updated?.id, payload);
      if (error) {
        alert('Erro ao atualizar filamento');
        return;
      }
      setFilaments(prev => prev?.map(f => f?.id === updated?.id ? mapFilamentRecord(data) : f));
      setShowEditDrawer(false);
      setFilamentToEdit(null);
    } catch (err) {
      console.error('Erro ao atualizar filamento:', err);
    }
  };

  const handleViewHistory = (filament) => {
    setSelectedFilamentForHistory(filament);
    setShowHistoryModal(true);
  };

  const handleMarkDepleted = async (filament) => {
    const confirmed = window.confirm(`Tem certeza que deseja excluir o filamento ${filament?.brand} ${filament?.colorName}?`);
    if (!confirmed) return;
    try {
      const { error } = await supabaseHelpers?.deleteFilament(filament?.id);
      if (error) {
        alert('Erro ao excluir filamento');
        return;
      }
      setFilaments(prev => prev?.filter(f => f?.id !== filament?.id));
    } catch (err) {
      console.error('Erro ao excluir filamento:', err);
    }
  };

  return (
    <>
      <PageLayout
        title="Inventário de Filamentos"
        subtitle="Gerencie seu estoque de filamentos e monitore níveis de estoque"
        iconName="Filament"
        headerRight={(
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => navigate('/calculator')}
              iconName="Calculator"
              iconPosition="left"
            >
              Calculadora
            </Button>
            <Button
              onClick={() => setShowAddDrawer(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Adicionar Filamento
            </Button>
          </div>
        )}
      >
        {/* Stats Section */}
        <FilamentStats filaments={filteredFilaments} />

        {/* Filters Section */}
        <FilamentFilters
          filters={filters}
          onFiltersChange={setFilters}
          availableColors={availableColors}
          availableMaterials={availableMaterials}
          availableBrands={availableBrands}
          availableSuppliers={availableSuppliers}
          totalCount={filaments?.length}
          filteredCount={filteredFilaments?.length}
        />

        {/* Grid Section */}
        <FilamentGrid
          filaments={filters?.groupDuplicates ? groupedFilaments : filteredFilaments}
          groupMode={!!filters?.groupDuplicates}
          loading={loading}
          onEdit={handleEditFilament}
          onViewHistory={handleViewHistory}
          onMarkDepleted={handleMarkDepleted}
          onAddFilament={() => setShowAddDrawer(true)}
        />
      </PageLayout>

      {/* Add Filament Drawer */}
      <AddFilamentDrawer
        isOpen={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        onSave={handleAddFilament}
        availableSuppliers={availableSuppliers}
      />

      {/* Edit Filament Drawer */}
      <EditFilamentDrawer
        isOpen={showEditDrawer}
        onClose={() => setShowEditDrawer(false)}
        onSave={handleSaveFilamentEdit}
        filament={filamentToEdit}
      />

      {/* Filament History Modal */}
      <FilamentHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        filament={selectedFilamentForHistory}
      />
    </>
  );

};

export default FilamentInventory;